/**
 * AddFlagsDialog — multi-select modal for the Flags chart tab
 * (PROD-827 chart write surfaces).
 *
 * Flutter ref: `patient_chart/.../widgets/add_flags_dialog.dart`
 * (L1-368).
 *
 * **Wire pattern**: ONE endpoint `POST /api/Patients/CreateFlag` with
 * an ARRAY body representing the patient's entire flag state. See
 * `CreateFlagBody` type doc for per-row semantics. Soft-delete is
 * just an entry with `isDeleted: true` in the array — no separate
 * delete endpoint.
 *
 * **UX (mirrors Flutter)**:
 * - Modal with "Add Flags" title, Cancel + Done buttons.
 * - Search input at the top (typeahead currently elided — Flutter's
 *   search uses the same master endpoint; we'll add when needed).
 * - Below: master flag catalog grouped by category, each flag a
 *   checkbox. Selected state computed from current patient flag state.
 * - Toggling a checkbox mutates a local working copy of the patient's
 *   flag state:
 *   - Existing active → unselected: `isDeleted: true` preserved metadata.
 *   - Existing tombstoned → re-selected: `isDeleted: false`.
 *   - Never-selected flag → newly selected: append entry with
 *     `identifier: null`, `createdBy: currentUser`, `createdDate: now`,
 *     `performerDetails: {}`.
 *   - Newly-selected flag → unselected before Save: remove from working
 *     copy (since it was never persisted server-side).
 * - "Done" → `POST /CreateFlag` with the entire working copy.
 *
 * **Ghost placeholder rows**: the GET response includes per-master-flag
 * placeholder rows (categoryName: null, createdBy: null, empty
 * performerDetails). We preserve them verbatim in the working copy and
 * ship them back on Save — they're backend-allocated state that we
 * never synthesise.
 *
 * **`dataAbsentReason: ""`** — Flutter `toJson` default (NOT the
 * `"n/a"` sentinel used elsewhere). Tracked under PROD-842.
 *
 * **`performerDetails: {}`** — empty object, NOT null and NOT
 * undefined. Flutter `toJson` defaults this when performerDetails is
 * null on new entries.
 */

import { useMemo, useState } from 'react';
import { Search, X } from 'lucide-react';
import { toast } from 'sonner';

import { Button, Modal, TextField, Skeleton } from '@scripsteam/scrips-signal-ds';
import { useAuthStore } from '@/auth/auth-store';
import { useFlagMasterList } from '@/patients/services/use-flag-master-list';
import { useCreateFlag } from '@/patients/services/use-create-flag';
import { formatLocalDatetimeForWire } from '@/shared/lib/date-format';
import type {
  FlagListResponse,
  FlagResponse,
  PatientFlag,
} from '@/types/patient-chart-api';
import { BrandSpinner } from '@scripsteam/scrips-signal-ds';

export interface AddFlagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  patientId: string;
  /** Current patient flag state from the GET — used to compute initial
   *  selections and to preserve ghost placeholder rows. */
  currentFlags: PatientFlag[];
}

export function AddFlagsDialog({
  open,
  onOpenChange,
  patientId,
  currentFlags,
}: AddFlagsDialogProps) {
  const user = useAuthStore((s) => s.user);
  const userId = user?.userId ?? '';

  const masterQ = useFlagMasterList(open);
  const createMut = useCreateFlag();

  const [query, setQuery] = useState('');
  // Working copy of the patient's flag state. Keyed by flag code so
  // each toggle hits O(1). Initialised on `open` transitions so the
  // user can Cancel out without persisting the working copy.
  const [working, setWorking] = useState<Map<number, PatientFlag>>(
    () => buildWorkingMap(currentFlags),
  );

  // Snapshot of codes that were ACTIVELY SELECTED at GET time —
  // i.e. real persisted records, not ghost placeholders. Used by the
  // toggle logic to decide whether an unselect should tombstone
  // (record existed server-side) or drop (record was a ghost
  // placeholder OR was added in this dialog session). Without this
  // snapshot we'd tombstone ghost-promoted-then-unselected entries,
  // which would send `isDeleted: true` to the backend for a flag the
  // patient never actually had — Samer PR #250 review §1.
  const [originalActiveCodes, setOriginalActiveCodes] = useState<Set<number>>(
    () => buildOriginalActiveSet(currentFlags),
  );

  // Reset working copy + active-codes snapshot whenever the dialog
  // re-opens with a fresh patient state (a parent refetch arrives
  // mid-session).
  useMemo(() => {
    if (open) {
      setWorking(buildWorkingMap(currentFlags));
      setOriginalActiveCodes(buildOriginalActiveSet(currentFlags));
      setQuery('');
    }
  }, [open, currentFlags]);

  const isSelected = (code: number | string): boolean => {
    const numeric = typeof code === 'string' ? Number(code) : code;
    const entry = working.get(numeric);
    if (!entry) return false;
    // "Selected" = isDeleted false AND a non-null categoryName +
    // non-null createdBy. The ghost placeholder rows have null on
    // both, so they don't count as selected.
    return (
      entry.isDeleted !== true &&
      entry.categoryName != null &&
      entry.createdBy != null
    );
  };

  const handleToggle = (master: FlagResponse) => {
    if (master.code == null) return;
    const numeric =
      typeof master.code === 'string' ? Number(master.code) : master.code;
    setWorking((prev) => {
      const next = new Map(prev);
      const existing = next.get(numeric);
      if (existing) {
        const isCurrentlySelected =
          existing.isDeleted !== true &&
          existing.categoryName != null &&
          existing.createdBy != null;
        if (isCurrentlySelected) {
          // User UNSELECTING. Three sub-cases, captured by the
          // `originalActiveCodes` snapshot (set of codes that were
          // ACTIVELY SELECTED at GET time — real persisted records
          // only):
          //
          // 1. Code IS in originalActiveCodes → real persisted record →
          //    `isDeleted: true` tombstone, preserve metadata.
          // 2. Code is NOT in originalActiveCodes → either a ghost
          //    placeholder we promoted this session OR a brand-new
          //    `identifier: null` row appended this session → DROP
          //    from working copy (no tombstone sent — Samer PR #250
          //    review §1: never send `isDeleted: true` for a row the
          //    patient never had server-side).
          //
          // The `identifier` presence check alone wasn't enough —
          // ghost placeholder rows from the GET carry a backend-
          // allocated GUID identifier even though the patient never
          // selected them. The snapshot is the only reliable signal.
          if (originalActiveCodes.has(numeric)) {
            next.set(numeric, { ...existing, isDeleted: true });
          } else {
            next.delete(numeric);
          }
        } else if (existing.isDeleted === true) {
          // Was tombstoned — user re-selecting. Flip back.
          next.set(numeric, {
            ...existing,
            isDeleted: false,
            // Some round-trips have ghost-style createdBy/null — if
            // the record never had a real recorder, stamp current
            // user so the wire row passes the active-state check.
            createdBy: existing.createdBy ?? userId,
            createdDate: existing.createdDate ?? nowIsoNoZ(),
            categoryName:
              existing.categoryName ?? master.categoryName ?? null,
          });
        } else {
          // Ghost placeholder row (categoryName null, createdBy null)
          // promoted to a fresh active selection.
          next.set(numeric, {
            ...existing,
            isDeleted: false,
            categoryName: master.categoryName ?? null,
            createdBy: userId,
            createdDate: nowIsoNoZ(),
            flagResponse: {
              code: numeric,
              display: master.display ?? null,
              categoryName: master.categoryName ?? null,
            },
            // `{}` empty object satisfies `PatientFlag['performerDetails']`
            // structurally — every field on that type is optional. See
            // `normalizeForWire` doc for the wire-shape contract.
            performerDetails: {},
          });
        }
      } else {
        // First-time selection: append a fresh row with
        // identifier:null so the server allocates a GUID on insert.
        next.set(numeric, {
          identifier: null,
          subject: patientId,
          encounter: null,
          author: null,
          // dataAbsentReason ships "" literal — see CreateFlagBody doc.
          // Type field is optional on read-side PatientFlag; we send
          // it explicitly here so the wire matches Flutter parity.
          startDate: null,
          endDate: null,
          isDeleted: false,
          categoryName: master.categoryName ?? null,
          flagResponse: {
            code: numeric,
            display: master.display ?? null,
            categoryName: master.categoryName ?? null,
          },
          createdDate: nowIsoNoZ(),
          createdBy: userId,
          performerDetails: {} as PatientFlag['performerDetails'],
        });
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!user || !userId) {
      toast.error("Couldn't identify the recording user (missing userId).");
      return;
    }
    const body = Array.from(working.values()).map(normalizeForWire);
    try {
      await createMut.mutateAsync({ patientId, body });
      const selectedCount = body.filter(
        (f) => f.isDeleted !== true && f.categoryName != null,
      ).length;
      toast.success(`Saved ${selectedCount} active flag${selectedCount === 1 ? '' : 's'}.`);
      onOpenChange(false);
    } catch {
      /* Global toast surfaces failure; keep dialog open. */
    }
  };

  // Master list grouped by category. Filter by search query.
  const groups = useMemo<FlagListResponse[]>(() => {
    const raw = masterQ.data ?? [];
    const q = query.trim().toLowerCase();
    if (!q) return raw;
    return raw
      .map((g) => ({
        ...g,
        flagResponse: (g.flagResponse ?? []).filter((f) =>
          (f.display ?? '').toLowerCase().includes(q),
        ),
      }))
      .filter((g) => (g.flagResponse?.length ?? 0) > 0);
  }, [masterQ.data, query]);

  const isPending = createMut.isPending;

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange(false)}
      title="Add flags"
      // The design's Add flags modal (chart-flags-add.html): size lg, Cancel secondary, Done primary.
      size="lg"
      variant="bleed"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button type="button" onClick={handleSubmit} disabled={isPending}>
            {isPending ? (
              <>
                <BrandSpinner size={16} label="Saving" className="mr-1" />
                Saving…
              </>
            ) : (
              'Done'
            )}
          </Button>
        </>
      }
    >
        <div className="px-6 py-4 border-b border-border-default">
          <div className="relative">
            <Search
              aria-hidden
              className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-text-hint"
            />
            <TextField
              type="text"
              placeholder="Search flags"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              inputClassName="pl-10 pr-10"
              autoComplete="off"
            />
            {query ? (
              <button
                type="button"
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center text-text-hint hover:text-text-primary"
                onClick={() => setQuery('')}
              >
                <X className="size-4" aria-hidden />
              </button>
            ) : null}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-hide px-6 py-4">
          {masterQ.isLoading ? (
            <div className="space-y-4">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : masterQ.isError ? (
            <p className="text-sm text-red-700">
              Could not load the flag list. Close and try again.
            </p>
          ) : groups.length === 0 ? (
            <p className="text-sm text-text-muted py-8 text-center">
              {query
                ? `No flags match "${query}".`
                : 'No flags available.'}
            </p>
          ) : (
            <div className="space-y-6">
              {groups.map((g) => (
                <FlagCategoryGroup
                  key={g.categoryName || 'uncategorized'}
                  group={g}
                  isSelected={isSelected}
                  onToggle={handleToggle}
                />
              ))}
            </div>
          )}
        </div>
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Category group
// ──────────────────────────────────────────────────────────────────────────

function FlagCategoryGroup({
  group,
  isSelected,
  onToggle,
}: {
  group: FlagListResponse;
  isSelected: (code: number | string) => boolean;
  onToggle: (master: FlagResponse) => void;
}) {
  const title = group.categoryName?.trim() || 'Other';
  const flags = group.flagResponse ?? [];
  return (
    <section>
      <h3 className="text-xs font-bold uppercase tracking-wide text-text-muted mb-2">
        {title}
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
        {flags.map((f, idx) => {
          if (f.code == null) return null;
          const selected = isSelected(f.code);
          return (
            <label
              key={`${f.code}-${idx}`}
              className={`flex items-start gap-2 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                selected
                  ? 'border-brand-primary bg-blue-100/40'
                  : 'border-border-default hover:bg-grey-100/50'
              }`}
            >
              <input
                type="checkbox"
                checked={selected}
                onChange={() => onToggle(f)}
                className="mt-1 h-4 w-4 rounded border-border-default accent-brand-primary"
              />
              <span
                className={`text-sm leading-tight ${
                  selected
                    ? 'font-medium text-brand-depth'
                    : 'text-text-primary'
                }`}
              >
                {f.display ?? '—'}
              </span>
            </label>
          );
        })}
      </div>
    </section>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────────────────

function buildWorkingMap(rows: PatientFlag[]): Map<number, PatientFlag> {
  const m = new Map<number, PatientFlag>();
  for (const r of rows) {
    const code = r.flagResponse?.code;
    if (code == null) continue;
    const numeric = typeof code === 'string' ? Number(code) : code;
    m.set(numeric, r);
  }
  return m;
}

/**
 * Codes that were ACTIVELY SELECTED at GET time. Used by `handleToggle`
 * to decide whether an unselect should tombstone (code IS in set) or
 * drop (code is NOT in set — ghost placeholder OR a row added this
 * session). See the toggle's case-3 comment for full rationale.
 *
 * The active-state check matches `isSelected`: a row is "active" only
 * when `isDeleted !== true` AND `categoryName != null` AND
 * `createdBy != null`. Ghost placeholders fail the categoryName check;
 * tombstones fail the isDeleted check.
 */
function buildOriginalActiveSet(rows: PatientFlag[]): Set<number> {
  const s = new Set<number>();
  for (const r of rows) {
    const code = r.flagResponse?.code;
    if (code == null) continue;
    const numeric = typeof code === 'string' ? Number(code) : code;
    const isActive =
      r.isDeleted !== true &&
      r.categoryName != null &&
      r.createdBy != null;
    if (isActive) s.add(numeric);
  }
  return s;
}

/**
 * `createdDate` stamp for a flag row: no-Z ISO read off the LOCAL clock.
 *
 * PROD-1778: was `new Date().toISOString().replace(/Z$/, '')`. `toISOString()`
 * converts to UTC first, so stripping the `Z` afterwards bakes the offset in
 * rather than removing it — the backend binds the literal, and a flag created
 * at 13:00 Asia/Dubai was audited as 09:00.
 */
function nowIsoNoZ(): string {
  return formatLocalDatetimeForWire(new Date());
}

/**
 * Defensive normalisation before shipping — guarantees the Flutter
 * parity wire shape on every row.
 *
 * **`performerDetails: {}` empty object type safety** (Samer PR #250
 * review §3): the read-side `PatientFlag.performerDetails` is typed
 * as `{ id?, name?, photo? } | null` (every field optional). An empty
 * object `{}` IS a structurally valid value for this type — TypeScript
 * accepts it without a cast. The cast below was historically present
 * for clarity but isn't strictly needed; we keep it to make the
 * "intentionally empty" wire contract explicit at the call site (and
 * to avoid the linter complaining about an unused-cast).
 *
 * If a future PR tightens `PatientFlag.performerDetails` to require
 * fields, this cast becomes load-bearing — fix would be to update the
 * wire shape doc on `CreateFlagBody` (PROD-842) and the read-side
 * type together. The wire still ships `{}` per Flutter `toJson`.
 */
function normalizeForWire(row: PatientFlag): PatientFlag {
  return {
    identifier: row.identifier ?? null,
    subject: row.subject ?? null,
    encounter: row.encounter ?? null,
    author: row.author ?? null,
    startDate: row.startDate ?? null,
    endDate: row.endDate ?? null,
    isDeleted: row.isDeleted === true,
    categoryName: row.categoryName ?? null,
    flagResponse: row.flagResponse
      ? {
          code: row.flagResponse.code ?? null,
          display: row.flagResponse.display ?? null,
          categoryName: row.flagResponse.categoryName ?? '',
        }
      : null,
    createdDate: row.createdDate ?? null,
    createdBy: row.createdBy ?? null,
    // Wire ships `{}` empty object on new rows (Flutter `toJson`
    // default when null). Existing rows round-trip the full shape
    // — the read-side `id`/`name`/`photo` fields are all optional
    // so `{}` structurally satisfies the type without a cast.
    performerDetails: row.performerDetails == null ? {} : row.performerDetails,
  };
}
